(() => {
  if (customElements.get('mdnmd-menu-header')) return;
  customElements.define('mdnmd-menu-header', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.layoutAnchor = this.parentElement;
      this.timers = new Map();
      this.menus = [...this.querySelectorAll('.mn-dropdown')];
      this.menus.forEach(menu => {
        menu.addEventListener('pointerenter', event => {
          clearTimeout(this.timers.get(menu));
          if (event.pointerType !== 'touch' && matchMedia('(hover:hover) and (min-width:1000px)').matches) {
            this.closeOthers(menu);
            menu.open = true;
            this.fit(menu);
          }
        }, options);
        menu.addEventListener('pointerleave', event => {
          if (event.pointerType === 'touch' || !matchMedia('(min-width:1000px)').matches) return;
          clearTimeout(this.timers.get(menu));
          this.timers.set(menu, setTimeout(() => {
            if (!menu.contains(document.activeElement)) this.close(menu);
          }, 250));
        }, options);
        menu.querySelector('summary').addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.closeOthers(menu);
          menu.open = true;
          this.fit(menu);
          menu.querySelector('.mn-panel summary,.mn-panel a')?.focus();
        }, options);
      });
      this.addEventListener('toggle', event => {
        if (!event.target.matches('.mn-dropdown')) return;
        if (event.target.open) { this.closeOthers(event.target); this.fit(event.target); }
        else event.target.querySelectorAll('.mn-nested[open]').forEach(item => { item.open = false; });
      }, { ...options, capture: true });
      // Avoid unrelated theme-level delegated menu click handlers. Links retain native navigation.
      this.addEventListener('click', event => {
        if (event.target.closest('.mn-dropdown')) event.stopPropagation();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const nested = event.target.closest('.mn-nested[open]');
        if (nested) {
          nested.open = false;
          nested.querySelector('summary').focus();
        } else {
          const menu = this.menus.find(item => item.open);
          if (!menu) return;
          this.close(menu);
          menu.querySelector('summary').focus();
        }
        event.preventDefault();
        event.stopPropagation();
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeOthers();
      }, options);
      // A pointer click can briefly leave focus on body in Safari. Only real outside focus closes.
      document.addEventListener('focusin', event => {
        if (!this.contains(event.target)) this.closeOthers();
      }, options);
      this.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.navHovered = true; this.syncAnnouncement(); } }, options);
      this.addEventListener('pointerleave', () => { this.navHovered = false; this.syncAnnouncement(); }, options);
      window.addEventListener('scroll', () => this.requestScroll(), { ...options, passive: true });
      window.addEventListener('resize', () => this.requestLayout(), options);
      window.addEventListener('load', () => this.requestLayout(), options);
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(name => {
        document.addEventListener(name, () => this.requestLayout(), options);
      });
      this.resizeObserver = new ResizeObserver(() => this.requestLayout());
      this.resizeObserver.observe(this);
      document.fonts?.ready.then(() => { if (this.isConnected) this.requestLayout(); });
      this.requestLayout();
    }
    close(menu) {
      clearTimeout(this.timers.get(menu));
      menu.open = false;
      menu.querySelectorAll('.mn-nested[open]').forEach(item => { item.open = false; });
    }
    closeOthers(except) { this.menus.forEach(menu => { if (menu !== except) this.close(menu); }); }
    fit(menu) {
      const panel = menu.querySelector('.mn-panel');
      if (!panel || !menu.open) return;
      panel.style.setProperty('--mn-shift', '0px');
      const rect = panel.getBoundingClientRect();
      const shift = rect.right > innerWidth - 12 ? innerWidth - 12 - rect.right : rect.left < 12 ? 12 - rect.left : 0;
      panel.style.setProperty('--mn-shift', `${shift}px`);
      panel.style.maxHeight = `${Math.max(60, innerHeight - rect.top - 12)}px`;
    }
    requestLayout() {
      if (this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => { this.layoutFrame = null; this.layout(); });
    }
    requestScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateScroll(); });
    }
    findAnnouncement() {
      // Shopify can put script/style nodes or group wrappers between sections.
      // Choose the nearest visible, preceding announcement by document order.
      const markers = [...document.querySelectorAll('[data-mn-announcement], [data-mde-announcement]')];
      for (let index = markers.length - 1; index >= 0; index--) {
        const marker = markers[index];
        if (!(marker.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
        if (!marker.getClientRects().length || getComputedStyle(marker).display === 'none') continue;
        return marker.closest('.shopify-section') || marker;
      }
      return null;
    }
    layout() {
      if (!this.isConnected) return;
      // Restore normal flow only within this frame so resize measurement is independent of fixed width.
      this.removeAttribute('data-pinned');
      this.restoreMainOverlap();
      this.layoutAnchor.style.marginBottom = '';
      this.layoutAnchor.style.height = '';
      const height = this.getBoundingClientRect().height;
      this.layoutAnchor.style.height = `${height}px`;
      const announcement = this.findAnnouncement();
      this.banner = null;
      if (this.dataset.home === 'true') {
        const main = document.querySelector('#MainContent') || document.querySelector('main');
        let candidate;
        if (main) {
          if (this.dataset.bannerSelector?.trim()) {
            try { candidate = main.querySelector(this.dataset.bannerSelector.trim()); } catch { /* Solid fallback for invalid selectors. */ }
          } else {
            candidate = [...main.querySelectorAll('.shopify-section'), ...main.children].find(item => item.getBoundingClientRect().height > height && getComputedStyle(item).display !== 'none');
          }
        }
        if (candidate) {
          const bannerBox = candidate.getBoundingClientRect();
          // Theme wrappers and padding must not disable banner opacity.
          // Overlay is explicitly enabled by the merchant for the homepage banner.
          if (bannerBox.height > height) {
            this.banner = candidate;
            const overlayTop = (announcement || this.layoutAnchor).getBoundingClientRect().top;
            const overlap = Math.max(height, bannerBox.top - overlayTop);
            // MainContent is a body-grid item in this theme. A negative margin
            // inside the header cannot remove the separate announcement row.
            // Offset the actual content grid item so the campaign covers both bars.
            this.mainOverlap = {
              node: main,
              value: main.style.getPropertyValue('margin-top'),
              priority: main.style.getPropertyPriority('margin-top')
            };
            const baseMargin = parseFloat(getComputedStyle(main).marginTop) || 0;
            main.style.setProperty('margin-top', `${baseMargin - overlap}px`);
          }
        }
      }
      if (this.observedBanner !== this.banner) {
        if (this.observedBanner) this.resizeObserver.unobserve(this.observedBanner);
        this.observedBanner = this.banner;
        if (this.banner) this.resizeObserver.observe(this.banner);
      }
      if (this.announcement !== announcement) {
        this.announcementAbort?.abort();
        if (this.announcement) { this.resizeObserver.unobserve(this.announcement); this.announcement.classList.remove('mn-managed-announcement'); this.announcement.removeAttribute('data-mn-hover'); this.announcement.removeAttribute('data-mn-over-banner'); }
        this.removeAttribute('data-announcement-hover');
        this.announcement = announcement;
        if (announcement) {
          announcement.classList.add('mn-managed-announcement');
          this.resizeObserver.observe(announcement);
          this.announcementAbort = new AbortController();
          announcement.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.setAttribute('data-announcement-hover', ''); this.syncAnnouncement(); } }, {signal:this.announcementAbort.signal});
          announcement.addEventListener('pointerleave', () => { this.removeAttribute('data-announcement-hover'); this.syncAnnouncement(); }, {signal:this.announcementAbort.signal});
        }
      }
      this.updateScroll();
    }
    restoreMainOverlap() {
      if (!this.mainOverlap) return;
      const {node, value, priority} = this.mainOverlap;
      if (value) node.style.setProperty('margin-top', value, priority);
      else node.style.removeProperty('margin-top');
      this.mainOverlap = null;
    }
    updateScroll() {
      if (!this.isConnected) return;
      const rect = this.layoutAnchor.getBoundingClientRect();
      this.style.setProperty('--mn-left', `${rect.left}px`);
      this.style.setProperty('--mn-fixed-width', `${rect.width}px`);
      this.toggleAttribute('data-pinned', rect.top <= 0);
      const navTop = this.getBoundingClientRect().top;
      const over = !!this.banner && this.banner.getBoundingClientRect().bottom > navTop;
      this.toggleAttribute('data-over-banner', over);
      this.toggleAttribute('data-past-banner', !!this.banner && !over);
      this.syncAnnouncement();
      this.menus.forEach(menu => { if (menu.open) this.fit(menu); });
    }
    syncAnnouncement() {
      if (!this.announcement) return;
      const style = getComputedStyle(this);
      ['--mn-hover-alpha','--mn-ink','--mn-hero-ink'].forEach(key => this.announcement.style.setProperty(key,style.getPropertyValue(key)));
      this.announcement.toggleAttribute('data-mn-over-banner', this.dataset.home === 'true' && !!this.banner && !this.hasAttribute('data-past-banner'));
      this.announcement.toggleAttribute('data-mn-hover', !!this.navHovered || this.hasAttribute('data-announcement-hover'));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.restoreMainOverlap();
      this.announcementAbort?.abort();
      if (this.announcement) { this.announcement.classList.remove('mn-managed-announcement'); this.announcement.removeAttribute('data-mn-hover'); this.announcement.removeAttribute('data-mn-over-banner'); }
      this.resizeObserver?.disconnect();
      this.timers?.forEach(timer => clearTimeout(timer));
      cancelAnimationFrame(this.layoutFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.layoutFrame = this.scrollFrame = null;
      this.layoutAnchor?.style.removeProperty('height');
      this.layoutAnchor?.style.removeProperty('margin-bottom');
    }
  });
})();


/* Mobile drawer + search reveal (below 1000px). Delegated so re-rendered sections keep working. */
(() => {
  if (window.__mnMobileBound) return;
  window.__mnMobileBound = true;
  const headers = () => document.querySelectorAll('mdnmd-menu-header');
  const setMenu = (header, open) => {
    header.toggleAttribute('data-menu-open', open);
    (header.closest('.shopify-section') || header.parentElement)?.classList.toggle('mn-drawer-open', open);
    header.querySelector('.mn-menu-toggle')?.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('mn-lock', open);
    if (!open) header.querySelectorAll('.mn-tabs details[open]').forEach(item => { item.open = false; });
  };
  const setSearch = (header, open) => {
    header.toggleAttribute('data-search-open', open);
    header.querySelector('.mn-search-toggle')?.setAttribute('aria-expanded', String(open));
    if (open) header.querySelector('.mn-search input')?.focus();
  };
  const closeAll = () => headers().forEach(header => { setMenu(header, false); setSearch(header, false); });
  document.addEventListener('click', event => {
    const header = event.target.closest('mdnmd-menu-header');
    if (!header) { headers().forEach(item => setSearch(item, false)); return; }
    if (event.target.closest('.mn-menu-toggle')) { setSearch(header, false); setMenu(header, !header.hasAttribute('data-menu-open')); }
    else if (event.target.closest('.mn-drawer-close, .mn-backdrop')) setMenu(header, false);
    else if (event.target.closest('.mn-search-toggle')) {
      setMenu(header, false);
      const input = header.querySelector('.mn-search input');
      if (header.hasAttribute('data-search-open') && input && input.value.trim()) { input.form.requestSubmit ? input.form.requestSubmit() : input.form.submit(); return; }
      setSearch(header, !header.hasAttribute('data-search-open'));
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    headers().forEach(header => {
      if (header.hasAttribute('data-menu-open')) { setMenu(header, false); header.querySelector('.mn-menu-toggle')?.focus(); }
      if (header.hasAttribute('data-search-open')) { setSearch(header, false); header.querySelector('.mn-search-toggle')?.focus(); }
    });
  });
  const desktop = matchMedia('(min-width:1000px)');
  desktop.addEventListener('change', () => { if (desktop.matches) closeAll(); });
  document.addEventListener('shopify:section:unload', closeAll);
})();
